import React, { useEffect } from "react";
import {
  CheckCircle2,
  CircleAlert,
  PackageCheck,
  PackageX,
  BadgeAlert,
} from "lucide-react";

const ModalConfirmReceiptOrder = ({
  isClient = false,
  isOpenConfirmReceiptOrder,
  showSuccessScreenConfirmReceiptOrder,
  closeModalConfirmReceiptOrder,
  formDataConfirmReceiptOrder,
  handleChange,
  handleSubmit,
  loadingConfirmReceiptOrder,
  selectedNegotiationConfirmReceiptOrder,
  errorsConfirmReceiptOrder,
  errorConfirmReceiptOrder,
}) => {
  const isReceiverLocked = Boolean(
    selectedNegotiationConfirmReceiptOrder?.receiver_data_locked,
  );

  useEffect(() => {
    if (isOpenConfirmReceiptOrder) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "unset";
    }

    return () => {
      document.documentElement.style.overflow = "auto";
      document.body.style.overflow = "unset";
    };
  }, [isOpenConfirmReceiptOrder]);

  if (!isOpenConfirmReceiptOrder) return null;

  const selectedReceiptStatus =
    formDataConfirmReceiptOrder?.receipt_status || "";

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background:
      "linear-gradient(180deg, rgba(15,23,42,0.72) 0%, rgba(15,23,42,0.86) 100%)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    overflowY: "auto",
    padding: "22px 14px",
  };

  const modalStyle = {
    width: "100%",
    maxWidth: "620px",
    margin: "auto",
    borderRadius: "24px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 30px 80px rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.55)",
    overflow: "hidden",
  };

  const headerStyle = {
    padding: "22px 24px",
    background: "linear-gradient(135deg,#1a7fe7,#19c4c9,#0ed079)",
    color: "#fff",
  };

  const titleStyle = {
    margin: 0,
    fontSize: "20px",
    fontWeight: 800,
    letterSpacing: "-0.02em",
  };

  const subtitleStyle = {
    margin: "6px 0 0",
    fontSize: "13px",
    color: "rgba(255,255,255,0.78)",
    lineHeight: 1.5,
  };

  const bodyStyle = {
    padding: "24px",
  };

  const alertStyle = {
    display: errorConfirmReceiptOrder ? "flex" : "none",
    alignItems: "center",
    gap: "10px",
    background:
      "linear-gradient(180deg, rgba(254,242,242,1) 0%, rgba(255,255,255,1) 100%)",
    color: "#b91c1c",
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid rgba(248,113,113,0.28)",
    boxShadow: "0 10px 24px rgba(185,28,28,0.08)",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "18px",
  };

  const infoCardStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    background:
      "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: "18px",
    padding: "16px",
    marginBottom: "18px",
    boxShadow: "0 10px 26px rgba(15,23,42,0.05)",
  };

  const tripLabelStyle = {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#64748b",
    marginBottom: "4px",
    fontWeight: 700,
  };

  const tripTextStyle = {
    margin: 0,
    color: "#0f172a",
    fontSize: "14px",
    lineHeight: 1.5,
    fontWeight: 600,
  };

  const sectionTitleStyle = {
    margin: "18px 0 12px",
    fontSize: "15px",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  };

  const optionGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
  };

  const getOptionCardStyle = (value, accent) => ({
    display: "flex",
    alignItems: "stretch",
    gap: "12px",
    width: "100%",
    borderRadius: "18px",
    border:
      selectedReceiptStatus === value
        ? `1.5px solid ${accent}`
        : "1px solid rgba(148,163,184,0.22)",
    background:
      selectedReceiptStatus === value
        ? "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)"
        : "#fff",
    boxShadow:
      selectedReceiptStatus === value
        ? "0 16px 36px rgba(15,23,42,0.10)"
        : "0 8px 22px rgba(15,23,42,0.05)",
    padding: "16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    opacity: 1,
  });

  const radioCircleStyle = (checked, accent) => ({
    width: "22px",
    height: "22px",
    borderRadius: "999px",
    border: `2px solid ${checked ? accent : "#cbd5e1"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "2px",
    flexShrink: 0,
    background: checked ? accent : "#fff",
    transition: "all 0.2s ease",
  });

  const radioInnerStyle = {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    background: "#fff",
  };

  const optionTextWrapStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  };

  const optionTitleStyle = {
    margin: 0,
    fontSize: "15px",
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.35,
  };

  const optionDescStyle = {
    margin: 0,
    fontSize: "13px",
    color: "#64748b",
    lineHeight: 1.45,
  };

  const inputErrorStyle = {
    marginTop: "8px",
    color: "#b91c1c",
    fontSize: "13px",
    fontWeight: 600,
  };

  const actionsStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "22px",
    flexWrap: "wrap",
  };

  const baseButtonStyle = {
    border: "none",
    borderRadius: "14px",
    padding: "12px 18px",
    minHeight: "46px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  };

  const ghostButtonStyle = {
    ...baseButtonStyle,
    background: "#e2e8f0",
    color: "#0f172a",
  };

  const primaryButtonStyle = {
    ...baseButtonStyle,
    color: "#fff",
    background: "linear-gradient(135deg,#1a7fe7,#19c4c9,#0ed079)",
    boxShadow: "0 14px 28px rgba(37,99,235,0.22)",
  };

  const successContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "26px 10px 8px",
  };

  const successTitleStyle = {
    margin: "14px 0 8px",
    fontSize: "20px",
    fontWeight: 800,
    color: "#0f172a",
  };

  const successTextStyle = {
    margin: 0,
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.6,
    maxWidth: "420px",
  };

  const options = [
    {
      value: "received_as_photos",
      title: "A encomenda chegou conforme as fotos",
      desc: "Selecione esta opção se o produto foi entregue em conformidade com o que foi anunciado ou enviado nas imagens.",
      icon: <PackageCheck size={20} />,
      accent: "#16a34a",
    },
    {
      value: "not_received",
      title: "A encomenda não chegou",
      desc: "Use esta opção caso a encomenda ainda não tenha sido recebida.",
      icon: <PackageX size={20} />,
      accent: "#dc2626",
    },
    {
      value: "arrived_with_defect",
      title: "A encomenda chegou com defeito",
      desc: "Selecione esta opção quando o item chegou, mas apresenta avarias, danos ou não está funcional.",
      icon: <BadgeAlert size={20} />,
      accent: "#ea580c",
    },
  ];

  return (
    <div
      onClick={closeModalConfirmReceiptOrder}
      style={overlayStyle}
      role="presentation"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-receipt-title"
      >
        <div style={headerStyle}>
          <h2 id="confirm-receipt-title" style={titleStyle}>
            Confirmar recebimento da encomenda
          </h2>
          <p style={subtitleStyle}>
            Selecione a situação real da encomenda para continuar.
          </p>
        </div>

        <div style={bodyStyle}>
          {showSuccessScreenConfirmReceiptOrder ? (
            <div style={successContainerStyle}>
              <CheckCircle2 size={88} color="#16a34a" />
              <h3 style={successTitleStyle}>Confirmação enviada com sucesso</h3>
              <p style={successTextStyle}>
                A informação foi registada com sucesso e será processada no
                sistema.
              </p>

              <button
                type="button"
                onClick={closeModalConfirmReceiptOrder}
                style={{
                  ...primaryButtonStyle,
                  marginTop: "20px",
                  minWidth: "160px",
                }}
              >
                Entendi
              </button>
            </div>
          ) : (
            <>
              {selectedNegotiationConfirmReceiptOrder?.trip && (
                <div style={infoCardStyle}>
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "14px",
                      background:
                        "linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(29,78,216,0.18) 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CircleAlert size={20} color="#1d4ed8" />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={tripLabelStyle}>Viagem</div>
                    <p style={tripTextStyle}>
                      {selectedNegotiationConfirmReceiptOrder.trip
                        ?.cidade_origem ||
                        selectedNegotiationConfirmReceiptOrder.trip?.de}{" "}
                      →{" "}
                      {selectedNegotiationConfirmReceiptOrder.trip
                        ?.cidade_destino ||
                        selectedNegotiationConfirmReceiptOrder.trip?.para}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={alertStyle}>
                  <CircleAlert size={18} />
                  <span>{errorConfirmReceiptOrder}</span>
                </div>

                <div style={sectionTitleStyle}>Como a encomenda chegou?</div>

                <div style={optionGridStyle}>
                  {options.map((option) => {
                    const checked = selectedReceiptStatus === option.value;

                    return (
                      <label
                        key={option.value}
                        htmlFor={option.value}
                        style={getOptionCardStyle(option.value, option.accent)}
                      >
                        <div style={radioCircleStyle(checked, option.accent)}>
                          {checked && <div style={radioInnerStyle} />}
                        </div>

                        <div style={{ marginTop: "2px", color: option.accent }}>
                          {option.icon}
                        </div>

                        <div style={optionTextWrapStyle}>
                          <p style={optionTitleStyle}>{option.title}</p>
                          <p style={optionDescStyle}>{option.desc}</p>
                        </div>

                        <input
                          id={option.value}
                          type="radio"
                          name="receipt_status"
                          value={option.value}
                          checked={checked}
                          onChange={handleChange}
                          disabled={loadingConfirmReceiptOrder}
                          style={{
                            position: "absolute",
                            opacity: 0,
                            pointerEvents: "none",
                          }}
                        />
                      </label>
                    );
                  })}
                </div>

                {errorsConfirmReceiptOrder?.receipt_status && (
                  <div style={inputErrorStyle}>
                    {errorsConfirmReceiptOrder.receipt_status}
                  </div>
                )}

                {formDataConfirmReceiptOrder?.is_receiver_readded && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "14px 16px",
                      borderRadius: "16px",
                      background:
                        "linear-gradient(180deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 100%)",
                      border: "1px solid rgba(96,165,250,0.25)",
                      color: "#1d4ed8",
                      fontSize: "14px",
                      fontWeight: 600,
                      boxShadow: "0 10px 24px rgba(37,99,235,0.06)",
                    }}
                  >
                    Adicione novamente os dados do destinatário.
                  </div>
                )}

                <div style={actionsStyle}>
                  <button
                    type="button"
                    onClick={closeModalConfirmReceiptOrder}
                    disabled={loadingConfirmReceiptOrder}
                    style={{
                      ...ghostButtonStyle,
                      opacity: loadingConfirmReceiptOrder ? 0.7 : 1,
                    }}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    disabled={loadingConfirmReceiptOrder}
                    style={{
                      ...primaryButtonStyle,
                      opacity: loadingConfirmReceiptOrder ? 0.8 : 1,
                      cursor: loadingConfirmReceiptOrder ? "wait" : "pointer",
                    }}
                  >
                    {loadingConfirmReceiptOrder ? "Enviando..." : "Confirmar"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmReceiptOrder;
